import React, { useEffect, useState } from 'react';
import './Portfolio.css';
import placeholderImage from '../assets/images/daniel-lambert-headshot.jpg';
import mopsImage1 from '../assets/images/marketing-operations-1.png';
import mopsImage2 from '../assets/images/marketing-operations-2.png';
import mopsImage3 from '../assets/images/marketing-operations-3.png';
import mopsImage4 from '../assets/images/marketing-operations-4.png';
import analyticsImage1 from '../assets/images/analytics-1.png';
import analyticsImage2 from '../assets/images/analytics-2.png';
import analyticsImage4 from '../assets/images/analytics-4.png';
import dbtLogo from '../assets/logos/dbt.png';
import langchainLogo from '../assets/logos/langchain.png';
import revLogo from '../assets/logos/rev.png';

const categories = [
  'Marketing Operations',
  'Analytics & Reporting',
];

const LOGOS = {
  dbt: { src: dbtLogo, alt: 'dbt Labs' },
  langchain: { src: langchainLogo, alt: 'LangChain' },
  rev: { src: revLogo, alt: 'Rev' },
};

const allProjects = {
  'Marketing Operations': [
    {
      title: 'Data Schemas',
      image: mopsImage1,
      companies: ['langchain', 'dbt'],
      description: 'Mapped and modeled the end-to-end customer data schema in Snowflake, connecting Salesforce, product, and event data into a single source of truth for activation.',
    },
    {
      title: 'Lead Scoring',
      image: mopsImage2,
      companies: ['langchain', 'dbt'],
      description: 'Built and tuned fit and intent scoring models in MadKudu to prioritize the highest-converting leads and route them to the right reps.',
    },
    {
      title: 'Intent & ABM',
      image: mopsImage3,
      companies: ['langchain', 'dbt'],
      description: 'Stood up account-level intent monitoring and ABM workflows, surfacing buying signals from target accounts to align sales and marketing outreach.',
    },
    {
      title: 'AI Chat',
      image: mopsImage4,
      companies: ['dbt', 'rev'],
      description: 'Designed and deployed an AI-powered website chat assistant that qualifies visitors in real time and routes hot conversations straight to reps.',
    },
  ],
  'Analytics & Reporting': [
    {
      title: 'Product Analytics',
      image: analyticsImage1,
      companies: ['rev'],
      description: 'Built conversion funnel dashboards tracking the full signup-to-checkout journey, uncovering drop-off points that shaped CRO experiments.',
    },
    {
      title: 'Campaign Analytics',
      image: analyticsImage2,
      companies: ['langchain', 'dbt'],
      description: 'Built a Campaign 360 reporting hub giving the team a single view into spend, pipeline, and funnel performance across every campaign.',
    },
    {
      title: 'Signal Based Selling',
      image: analyticsImage4,
      companies: ['langchain', 'dbt'],
      description: 'Built a signal-based prospecting dashboard surfacing intent, keyword research, and account activity to help reps prioritize outbound in real time.',
    },
  ],
};

const flatProjects = categories.flatMap((category) =>
  allProjects[category].map((project) => ({ ...project, category }))
);

const Portfolio = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const total = flatProjects.length;
  const current = flatProjects[currentIndex];

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + total) % total);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % total);
  };

  useEffect(() => {
    if (!isLightboxOpen) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsLightboxOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen]);

  return (
    <section className="portfolio-section" id="portfolio">
      <div className="portfolio-intro">
        <h2>Explore My Work</h2>
        <p>
          Over the years, I've enjoyed working across marketing operations and
          analytics. Here are some of the most interesting projects I've worked on.
        </p>
      </div>

      <div className="portfolio-carousel">
        <button
          className="carousel-arrow prev"
          onClick={goToPrev}
          aria-label="Previous project"
        >
          ‹
        </button>

        <div className="portfolio-card">
          <div className="portfolio-card-content">
            <div className="portfolio-logos">
              {current.companies.map((key) => (
                <img
                  key={key}
                  src={LOGOS[key].src}
                  alt={LOGOS[key].alt}
                  title={LOGOS[key].alt}
                  className="portfolio-logo"
                />
              ))}
            </div>
            <span className="portfolio-tag">{current.category}</span>
            <h3>{current.title}</h3>
            <p className="portfolio-description">{current.description}</p>
          </div>
          <button
            className="portfolio-card-image"
            onClick={() => setIsLightboxOpen(true)}
            aria-label={`Expand image for ${current.title}`}
          >
            <img src={current.image} alt={current.title} />
          </button>
        </div>

        <button
          className="carousel-arrow next"
          onClick={goToNext}
          aria-label="Next project"
        >
          ›
        </button>
      </div>

      <div className="carousel-counter">
        {currentIndex + 1} / {total}
      </div>

      {isLightboxOpen && (
        <div
          className="lightbox-overlay"
          onClick={() => setIsLightboxOpen(false)}
        >
          <button
            className="lightbox-close"
            onClick={() => setIsLightboxOpen(false)}
            aria-label="Close image"
          >
            ×
          </button>
          <img
            className="lightbox-image"
            src={current.image}
            alt={current.title}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};

export default Portfolio; 